import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box, Chip, CircularProgress, FormControlLabel, Link, Paper, Switch, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  useReactTable, getCoreRowModel, getSortedRowModel, flexRender, ColumnDef, SortingState,
} from '@tanstack/react-table';
import { outcomesApi, OutcomesLocationSummary } from '../api/services';
import { CLASS_LABEL, CLASS_ORDER } from '../components/outcomes/DigestOutcomes';

// Edge Reviews index: EVERY monitored location with its review-cycle state —
// including locations with nothing actionable (the answer is then "nothing
// yet, N groups still maturing", not an empty filter result). Doubles as the
// check-in planning view via the last/next review dates.
//
// Digest columns (sc-6100): the fleet view of the sc-6064 acceptance mix,
// fetched separately from the locations query (the replica sweep must never
// block the index render) and merged by location_id. undefined = "don't
// know" (still loading, unlinked account, or replica down) — rendered as —
// and sorted last, never as a fabricated 0%.

const formatDate = (value: string | null) =>
  value ? new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—';

// Date-ONLY strings (`since`) are calendar dates, not instants — parse as
// local y/m/d (the DigestOutcomes convention) so the label can't shift a day.
const fmtDay = (day: string) => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

type IndexRow = OutcomesLocationSummary & {
  digest_count?: number;
  digest_accept_pct?: number; // 0..1; undefined = unknown or zero digests
  digest_action_pct?: number;
  digest_summary?: Record<string, number> | null;
};

// Stacked two-line column header (sc-6100 layout ruling: line-broken words,
// no group band row).
const TwoLineHeader: React.FC<{ top: string; bottom: string; tooltip: string }> = ({ top, bottom, tooltip }) => (
  <Tooltip title={tooltip} placement="top">
    <Box sx={{ lineHeight: 1.25, textAlign: 'center' }}>
      {top}<br />{bottom}
    </Box>
  </Tooltip>
);

// The full seven-class mix, in detail-page order — the % cells' tooltip, so
// the fleet number always explains itself in the detail page's vocabulary.
const mixTitle = (summary: Record<string, number>, count: number) => (
  <Box sx={{ whiteSpace: 'nowrap' }}>
    {CLASS_ORDER.map((klass) => (
      <Typography key={klass} variant="caption" display="block">
        {CLASS_LABEL[klass]}: {summary[klass] ?? 0}
        {count > 0 ? ` · ${Math.round(((summary[klass] ?? 0) / count) * 100)}%` : ''}
      </Typography>
    ))}
  </Box>
);

const DimDash: React.FC = () => <Typography variant="caption" color="text.secondary">—</Typography>;

const PctCell: React.FC<{ value?: number; summary?: Record<string, number> | null; count?: number }> = (
  { value, summary, count },
) => {
  if (value === undefined || !summary || !count) return <DimDash />;
  return (
    <Tooltip title={mixTitle(summary, count)} placement="top">
      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
        {Math.round(value * 100)}%
      </Typography>
    </Tooltip>
  );
};

const EdgeReviewsPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [onlyOpen, setOnlyOpen] = useState(false);
  // Default sort = most recently reviewed first (sc-5979) — deterministic on every
  // visit, so back-navigation landing on a fresh mount reads the same as before.
  const [sorting, setSorting] = useState<SortingState>([{ id: 'last_reviewed_on', desc: true }]);

  const { data: locations = [], isLoading } = useQuery({
    queryKey: ['outcomes-locations'],
    queryFn: outcomesApi.locations,
    staleTime: 60000,
  });
  // Fleet digest acceptance (sc-6100) — parallel query; the index renders
  // without it and the digest cells fill in when it lands.
  const { data: digestData } = useQuery({
    queryKey: ['outcomes-digest-acceptance'],
    queryFn: outcomesApi.digestAcceptance,
    staleTime: 5 * 60 * 1000,
  });

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return locations.filter((row) => {
      if (onlyOpen && row.open_counts.set + row.open_counts.raise + row.open_counts.lower === 0) return false;
      if (!needle) return true;
      return row.name.toLowerCase().includes(needle) ||
        (row.organization?.name || '').toLowerCase().includes(needle);
    });
  }, [locations, search, onlyOpen]);

  // Merge the digest summaries INTO the row objects (not a lookup at render)
  // so the table re-sorts when the parallel query lands. A null summary from
  // the server (unlinked / replica down) keeps the percentages undefined.
  const rows = useMemo<IndexRow[]>(() => {
    const byLocation = new Map((digestData?.locations ?? []).map((r) => [r.location_id, r]));
    return filtered.map((row) => {
      const digest = byLocation.get(row.location_id);
      if (!digest) return row;
      const { digest_count: count, summary } = digest;
      const pct = (numerator: number) => (summary && count > 0 ? numerator / count : undefined);
      return {
        ...row,
        digest_count: count,
        digest_accept_pct: pct(summary?.accepted_as_is ?? 0),
        digest_action_pct: pct(count - (summary?.no_action ?? 0)),
        digest_summary: summary,
      };
    });
  }, [filtered, digestData]);

  const sinceLabel = digestData?.since ? `since ${fmtDay(digestData.since)}` : 'in the last 6 months';

  const columns = useMemo<ColumnDef<IndexRow>[]>(() => [
    {
      accessorKey: 'name', header: 'Location', size: 220,
      cell: ({ row }) => (
        <Box>
          {/* Real anchor (sc-5979): cmd/middle/right-click open the review in a new
              tab natively. stopPropagation keeps a plain left-click from ALSO firing
              the row onClick (double navigation); RouterLink ignores modified clicks
              so those fall through to the browser default. */}
          <Link
            component={RouterLink}
            to={`/outcomes/locations/${row.original.location_id}`}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            underline="hover"
            color="inherit"
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.original.name}</Typography>
          </Link>
          <Typography variant="caption" color="text.secondary">{row.original.organization?.name}</Typography>
        </Box>
      ),
    },
    {
      id: 'digest_count', size: 90,
      header: () => (
        <TwoLineHeader top="#" bottom="Digests" tooltip={`Sent restock digests ${sinceLabel}`} />
      ),
      accessorFn: (row) => row.digest_count,
      sortUndefined: 'last',
      cell: ({ getValue }) => {
        const count = getValue() as number | undefined;
        if (count === undefined) return <DimDash />;
        return (
          <Typography variant="body2" color={count === 0 ? 'text.secondary' : undefined}
            sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {count}
          </Typography>
        );
      },
    },
    {
      id: 'digest_accept_pct', size: 90,
      header: () => (
        <TwoLineHeader
          top="Digest" bottom="accept %"
          tooltip={`Digests accepted exactly as sent — every line at full quantity via direct quote confirmation, no extra SKUs (${sinceLabel})`}
        />
      ),
      accessorFn: (row) => row.digest_accept_pct,
      sortUndefined: 'last',
      cell: ({ row }) => (
        <PctCell value={row.original.digest_accept_pct} summary={row.original.digest_summary}
          count={row.original.digest_count} />
      ),
    },
    {
      id: 'digest_action_pct', size: 90,
      header: () => (
        <TwoLineHeader
          top="Digest" bottom="action %"
          tooltip="Digests with ANY qualifying order response, including late action — everything except no-action. Digest-anchored: deliberately different from the quote-anchored HVA rate."
        />
      ),
      accessorFn: (row) => row.digest_action_pct,
      sortUndefined: 'last',
      cell: ({ row }) => (
        <PctCell value={row.original.digest_action_pct} summary={row.original.digest_summary}
          count={row.original.digest_count} />
      ),
    },
    {
      // sc-6100: plain sortable total (the raise/set/lower breakdown lives on
      // the detail page).
      id: 'open_total', header: 'Open recs', size: 90,
      accessorFn: (row) => row.open_counts.set + row.open_counts.raise + row.open_counts.lower,
      cell: ({ getValue }) => {
        const total = getValue() as number;
        return (
          <Typography variant="body2" color={total === 0 ? 'text.secondary' : undefined}
            sx={{ fontWeight: total === 0 ? undefined : 600, fontVariantNumeric: 'tabular-nums' }}>
            {total}
          </Typography>
        );
      },
    },
    {
      accessorKey: 'avoidable_expediting_value_cents', header: 'Expediting cost', size: 120,
      cell: ({ getValue }) => {
        const cents = getValue() as number | null;
        if (!cents) return <DimDash />;
        return (
          <Tooltip title="Avoidable expedited-freight cost, last 6 months (raise / set groups)">
            <Chip size="small" label={`$${Math.round(cents / 100).toLocaleString()}`}
              sx={{ bgcolor: '#EEF5F8', color: '#006E8F', fontWeight: 600 }} />
          </Tooltip>
        );
      },
    },
    {
      accessorKey: 'dead_stock_value_cents', header: 'Dead stock', size: 100,
      cell: ({ getValue }) => {
        const cents = getValue() as number | null;
        if (!cents) return <DimDash />;
        return (
          <Tooltip title="Capital tied up in reduce-recommendation stock (groups with ≥6 weeks of history)">
            <Chip size="small" label={`$${Math.round(cents / 100).toLocaleString()}`}
              sx={{ bgcolor: '#FBECE0', color: '#C5641A', fontWeight: 600 }} />
          </Tooltip>
        );
      },
    },
    {
      // accessorFn coerces null → undefined so sortUndefined applies: never-reviewed
      // locations sort LAST in either direction instead of floating above real
      // dates under the desc default (sc-5979). ISO date strings sort correctly.
      id: 'last_reviewed_on', header: 'Last reviewed', size: 120,
      accessorFn: (row) => row.last_reviewed_on ?? undefined,
      sortUndefined: 'last',
      cell: ({ row }) => {
        const value = row.original.last_reviewed_on;
        if (!value) return <Chip size="small" variant="outlined" color="warning" label="awaiting first" />;
        return formatDate(value);
      },
    },
    {
      accessorKey: 'next_review_on', header: 'Next review', size: 120,
      cell: ({ getValue }) => formatDate(getValue() as string | null),
    },
  ], [sinceLabel]);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => String(row.location_id),
  });

  return (
    <Box sx={{ height: '100%', width: '100%', p: 2 }}>
      <Typography variant="h4" gutterBottom>Edge Reviews</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Demand-driven stocking-level reviews, recomputed every 6 weeks per location.
        Open a location for its check-in detail.
      </Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          label="Search location / organization" size="small" sx={{ minWidth: 280 }}
          value={search} onChange={(e) => setSearch(e.target.value)}
        />
        <FormControlLabel
          control={<Switch checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} />}
          label="Only locations with open recommendations"
        />
        {isLoading && <CircularProgress size={18} />}
      </Box>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 260px)' }}>
          <Table stickyHeader size="small" sx={{
            // sc-5939: center every column; Location (first) stays left-aligned.
            '& tbody td': { textAlign: 'center' },
            '& tbody td:first-of-type': { textAlign: 'left' },
          }}>
            <TableHead>
              {table.getHeaderGroups().map((hg) => (
                <TableRow key={hg.id}>
                  {hg.headers.map((header) => (
                    <TableCell
                      key={header.id}
                      sx={{
                        fontWeight: 'bold', backgroundColor: 'primary.main', color: 'primary.contrastText',
                        cursor: header.column.getCanSort() ? 'pointer' : 'default', userSelect: 'none',
                      }}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: header.index === 0 ? 'flex-start' : 'center', gap: 1 }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{ asc: ' ↑', desc: ' ↓' }[header.column.getIsSorted() as string] ?? null}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id} hover sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/outcomes/locations/${row.original.location_id}`)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              {!isLoading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columns.length}>
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      No monitored locations match.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default EdgeReviewsPage;
